import React from "react";
import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { akilItemPrimary } from "../../lib/aiListDisplay";

const BAND_RENK = {
    "30+": "bg-rose-500/15 text-rose-200 border-rose-500/35",
    "21+": "bg-orange-500/15 text-orange-200 border-orange-500/35",
    "15+": "bg-amber-500/15 text-amber-200 border-amber-500/35",
    TAHKIM: "bg-purple-500/15 text-purple-200 border-purple-500/35",
    BASVURU: "bg-sky-500/15 text-sky-200 border-sky-500/35",
};

function Badge({ children, tone = "default" }) {
    const cls =
        tone === "yeni"
            ? "bg-emerald-500/15 text-emerald-200 border-emerald-500/35"
            : tone === "warn"
              ? "bg-amber-500/15 text-amber-200 border-amber-500/35"
              : "bg-white/5 text-slate-400 border-white/10";
    return (
        <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold border ${cls}`}>
            {children}
        </span>
    );
}

export function BrifingSection({ title, subtitle, action, children, accent = "default" }) {
    const border =
        accent === "tahkim"
            ? "border-purple-500/30 bg-gradient-to-br from-purple-500/[0.08] to-[#121A2F]/90"
            : accent === "alert"
              ? "border-rose-500/25 bg-rose-500/[0.04]"
              : "border-white/10 bg-[#121A2F]/60";
    return (
        <section className={`rounded-2xl border ${border} overflow-hidden`}>
            <div className="px-4 py-3 border-b border-white/10 flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h2 className="text-sm font-semibold text-white">{title}</h2>
                    {subtitle ? <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p> : null}
                </div>
                {action}
            </div>
            <div className="p-0">{children}</div>
        </section>
    );
}

export function BrifingDataTable({ columns, rows, emptyText = "Kayıt yok.", compact = false }) {
    if (!rows?.length) {
        return <p className="text-sm text-slate-500 px-4 py-8 text-center">{emptyText}</p>;
    }
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
                <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-slate-500 bg-[#0B0F19]/70">
                        {columns.map((col) => (
                            <th
                                key={col.key}
                                className={`text-left font-semibold px-4 py-2.5 ${col.className || ""}`}
                            >
                                {col.label}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, idx) => (
                        <tr
                            key={row.id || row.sigorta_dosyasi_id || row.href || idx}
                            className="border-t border-white/[0.06] hover:bg-white/[0.03] transition-colors"
                        >
                            {columns.map((col) => (
                                <td
                                    key={col.key}
                                    className={`px-4 ${compact ? "py-2" : "py-2.5"} align-middle ${col.className || ""}`}
                                >
                                    {col.render ? col.render(row) : row[col.key] ?? "—"}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export const TAHKIM_GECIS_COLUMNS = [
    {
        key: "muvekkil",
        label: "Müvekkil / Dosya",
        render: (row) => (
            <div className="min-w-[180px]">
                {row.href ? (
                    <Link to={row.href} className="text-slate-100 hover:text-[#D4AF37] font-medium">
                        {akilItemPrimary(row)}
                    </Link>
                ) : (
                    <span className="text-slate-100 font-medium">{akilItemPrimary(row)}</span>
                )}
                <div className="text-[11px] text-slate-500 mt-0.5">
                    {row.dosya_no}
                    {row.plaka ? ` · ${row.plaka}` : ""}
                </div>
            </div>
        ),
    },
    {
        key: "sigorta",
        label: "Sigorta",
        className: "hidden md:table-cell",
        render: (row) => <span className="text-slate-400 text-xs">{row.sigorta || "—"}</span>,
    },
    {
        key: "konu",
        label: "Konu",
        className: "hidden lg:table-cell",
        render: (row) => <span className="text-slate-400 text-xs">{row.konu || "—"}</span>,
    },
    {
        key: "paket",
        label: "Paket",
        render: (row) => (
            <span className="text-xs text-slate-400">
                {row.paket_sayisi ? `${row.paket_sayisi} adet` : "—"}
            </span>
        ),
    },
    {
        key: "gecis",
        label: "Durum",
        render: (row) => (
            <div className="flex flex-col gap-1">
                {row.yeni_gecis ? <Badge tone="yeni">Yeni</Badge> : null}
                <span className="text-[11px] text-slate-500">{row.gecis_notu || row.durum}</span>
            </div>
        ),
    },
    {
        key: "link",
        label: "",
        className: "text-right w-24",
        render: (row) => (
            <div className="flex justify-end gap-1">
                {row.tahkim_href ? (
                    <Link
                        to={row.tahkim_href}
                        className="text-[10px] px-2 py-1 rounded border border-purple-500/30 text-purple-200 hover:bg-purple-500/10"
                    >
                        Tahkim
                    </Link>
                ) : null}
                <Link
                    to={`/yeni/muvekkil-merkezi`}
                    className="text-[10px] p-1 rounded border border-white/10 text-slate-500 hover:text-[#D4AF37]"
                    title="Tahkim Akıl"
                >
                    <ExternalLink className="h-3 w-3" />
                </Link>
            </div>
        ),
    },
];

export const TAHKIM_ADAY_COLUMNS = [
    {
        key: "muvekkil",
        label: "Müvekkil / Dosya",
        render: (row) => (
            <div>
                <Link to={row.href} className="text-slate-100 hover:text-[#D4AF37] font-medium">
                    {akilItemPrimary(row)}
                </Link>
                <div className="text-[11px] text-slate-500">{row.dosya_no}</div>
            </div>
        ),
    },
    {
        key: "gecen",
        label: "Bekleme",
        render: (row) => (
            <span className="text-slate-300 font-medium">{row.gecen_sure ?? "—"} gün</span>
        ),
    },
    {
        key: "band",
        label: "Uyarı",
        render: (row) => {
            const band = row.uyari_band || "15+";
            return (
                <span
                    className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold border ${BAND_RENK[band] || BAND_RENK["15+"]}`}
                >
                    {band}
                </span>
            );
        },
    },
    {
        key: "sigorta",
        label: "Sigorta",
        className: "hidden md:table-cell",
        render: (row) => <span className="text-xs text-slate-500">{row.sigorta || "—"}</span>,
    },
    {
        key: "link",
        label: "",
        className: "text-right",
        render: (row) => (
            <Link to={row.href} className="text-xs text-[#D4AF37] hover:underline">
                Dosya →
            </Link>
        ),
    },
];

export const ONCELIK_COLUMNS = [
    {
        key: "muvekkil",
        label: "Dosya",
        render: (row) => (
            <Link to={row.href} className="text-slate-100 hover:text-[#D4AF37]">
                {akilItemPrimary(row)}
            </Link>
        ),
    },
    {
        key: "sub",
        label: "Neden",
        render: (row) => <span className="text-xs text-slate-500">{row.sub || row.status}</span>,
    },
    {
        key: "skor",
        label: "Skor",
        className: "text-right w-16",
        render: (row) => (
            <span className="text-[#F3E5AB] font-display">{row.skor ?? row.status ?? "—"}</span>
        ),
    },
];

export const OPERASYON_COLUMNS = [
    {
        key: "muvekkil",
        label: "Kayıt",
        render: (row) => (
            <Link to={row.href} className="text-slate-100 hover:text-[#D4AF37]">
                {akilItemPrimary(row)}
            </Link>
        ),
    },
    {
        key: "sub",
        label: "Detay",
        render: (row) => <span className="text-xs text-slate-500">{row.sub || row.status}</span>,
    },
    {
        key: "link",
        label: "",
        className: "text-right",
        render: (row) =>
            row.href ? (
                <Link to={row.href} className="text-xs text-[#D4AF37]">
                    Aç →
                </Link>
            ) : null,
    },
];
